import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, AlertTriangle, RefreshCw, RotateCcw, Undo2, Banknote } from 'lucide-react';
import { Button, Card } from '../components';
import {
  fetchStripeIncidents,
  resolveStripeIncident,
  type StripeIncident,
  type IncidentResolution,
  type IncidentType,
} from '../services/stripeIncidentApi';

const TYPE_ICON: Record<IncidentType, React.ComponentType<{ className?: string }>> = {
  dispute: AlertTriangle,
  refund: Undo2,
  payout_failed: Banknote,
};

/**
 * Een dispute is het duurste geval: `losses_collector` staat op `application`,
 * dus dat bedrag is al van de platformbalans af. Daarom rood, niet oranje.
 */
const TYPE_KLEUR: Record<IncidentType, string> = {
  dispute: 'bg-red-50 text-red-700 border-red-200',
  refund: 'bg-orange-50 text-orange-700 border-orange-200',
  payout_failed: 'bg-yellow-50 text-yellow-800 border-yellow-200',
};

const AFHANDELINGEN: IncidentResolution[] = ['recovered', 'written_off', 'no_action_needed'];

export const AdminStripeIncidentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();

  const [scope, setScope] = useState<'open' | 'alles'>('open');
  const [afhandelModal, setAfhandelModal] = useState<StripeIncident | null>(null);
  const [gekozenAfhandeling, setGekozenAfhandeling] = useState<IncidentResolution>('recovered');
  const [notitie, setNotitie] = useState('');

  const { data, isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: ['admin-stripe-incidents', scope],
    queryFn: async () => {
      const res = await fetchStripeIncidents(scope);
      const payload = res?.data?.data ?? res?.data;
      return (Array.isArray(payload) ? payload : []) as StripeIncident[];
    },
  });

  const incidenten = useMemo(() => data ?? [], [data]);

  const totalen = useMemo(() => {
    const som = (type: IncidentType) =>
      incidenten
        .filter((i) => i.incident_type === type && i.resolution === 'open')
        .reduce((acc, i) => acc + parseFloat(i.amount || '0'), 0);

    return {
      dispute: som('dispute'),
      refund: som('refund'),
      payout_failed: som('payout_failed'),
      // Alleen disputes en refunds kosten Belagom geld; een mislukte payout
      // staat nog gewoon op de platformbalans.
      blootstelling: som('dispute') + som('refund'),
    };
  }, [incidenten]);

  const afhandelMutatie = useMutation({
    mutationFn: ({ id, resolution, notes }: { id: string; resolution: IncidentResolution; notes: string }) =>
      resolveStripeIncident(id, resolution, notes.trim() || undefined),
    onSuccess: () => {
      toast.success(t('stripeIncidents.toastAfgehandeld', { defaultValue: 'Incident afgehandeld' }));
      queryClient.invalidateQueries({ queryKey: ['admin-stripe-incidents'] });
      sluitModal();
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          t('stripeIncidents.toastFout', { defaultValue: 'Afhandelen mislukt' })
      );
    },
  });

  const sluitModal = () => {
    setAfhandelModal(null);
    setNotitie('');
    setGekozenAfhandeling('recovered');
  };

  const euro = (bedrag: string | number | null) =>
    new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(
      typeof bedrag === 'number' ? bedrag : parseFloat(bedrag || '0')
    );

  const datum = (iso: string) =>
    new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('stripeIncidents.terug', { defaultValue: 'Terug naar dashboard' })}
        </button>

        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('stripeIncidents.titel', { defaultValue: 'Terugboekingen en mislukte uitbetalingen' })}
            </h1>
            <p className="text-sm text-gray-500 mt-1 max-w-3xl">
              {t('stripeIncidents.uitleg', {
                defaultValue:
                  'Betalingen die zijn teruggedraaid nadat de professional al was uitbetaald. BeLagom draagt dit verlies, dus elke regel hier vraagt om een besluit: terugvorderen of afboeken.',
              })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              {(['open', 'alles'] as const).map((optie) => (
                <button
                  key={optie}
                  onClick={() => setScope(optie)}
                  className={`px-3 py-1.5 text-sm ${
                    scope === optie ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {t(`stripeIncidents.scope.${optie}`, { defaultValue: optie === 'open' ? 'Openstaand' : 'Alles' })}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isRefetching}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefetching ? 'animate-spin' : ''}`} />
              {t('stripeIncidents.ververs', { defaultValue: 'Verversen' })}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <p className="text-sm text-gray-500">
              {t('stripeIncidents.kpiBlootstelling', { defaultValue: 'Openstaand verlies' })}
            </p>
            <p className="text-2xl font-bold text-red-600 mt-1">{euro(totalen.blootstelling)}</p>
            <p className="text-xs text-gray-400 mt-1">
              {t('stripeIncidents.kpiBlootstellingHint', { defaultValue: 'Disputes + refunds, nog niet afgehandeld' })}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500">
              {t('stripeIncidents.kpiDisputes', { defaultValue: 'Disputes' })}
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{euro(totalen.dispute)}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500">{t('stripeIncidents.kpiRefunds', { defaultValue: 'Refunds' })}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{euro(totalen.refund)}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-gray-500">
              {t('stripeIncidents.kpiPayouts', { defaultValue: 'Mislukte uitbetalingen' })}
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{euro(totalen.payout_failed)}</p>
            <p className="text-xs text-gray-400 mt-1">
              {t('stripeIncidents.kpiPayoutsHint', { defaultValue: 'Geld staat nog op de platformbalans' })}
            </p>
          </Card>
        </div>

        <Card className="overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center text-gray-500">
              {t('stripeIncidents.laden', { defaultValue: 'Laden…' })}
            </div>
          ) : isError ? (
            <div className="p-12 text-center text-red-600">
              {t('stripeIncidents.fout', { defaultValue: 'Ophalen van de incidenten is mislukt.' })}
            </div>
          ) : incidenten.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-900 font-medium">
                {t('stripeIncidents.leegTitel', { defaultValue: 'Geen openstaande incidenten' })}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {scope === 'open'
                  ? t('stripeIncidents.leegUitleg', {
                      defaultValue: 'Er is niets teruggeboekt en er zijn geen uitbetalingen gestrand.',
                    })
                  : t('stripeIncidents.leegUitlegAlles', {
                      defaultValue: 'Er zijn nog nooit incidenten geregistreerd.',
                    })}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      ['type', 'Type'],
                      ['datum', 'Datum'],
                      ['bedrag', 'Bedrag'],
                      ['factuur', 'Factuur'],
                      ['professional', 'Professional'],
                      ['reden', 'Reden'],
                      ['actie', ''],
                    ].map(([key, fallback]) => (
                      <th
                        key={key}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {t(`stripeIncidents.kolom.${key}`, { defaultValue: fallback })}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {incidenten.map((incident) => {
                    const Icon = TYPE_ICON[incident.incident_type];
                    return (
                      <tr key={incident.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs font-medium ${
                              TYPE_KLEUR[incident.incident_type]
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            {t(`stripeIncidents.type.${incident.incident_type}`, {
                              defaultValue: incident.incident_type,
                            })}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {datum(incident.created_at)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {euro(incident.amount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {incident.invoice_number ?? (
                            <span className="text-amber-600">
                              {t('stripeIncidents.geenFactuur', { defaultValue: 'niet gekoppeld' })}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {incident.professional_name ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <span className="block">{incident.reason ?? '—'}</span>
                          {incident.stripe_status && (
                            <span className="text-xs text-gray-400">{incident.stripe_status}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {incident.resolution === 'open' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setAfhandelModal(incident);
                                setGekozenAfhandeling('recovered');
                                setNotitie('');
                              }}
                            >
                              {t('stripeIncidents.afhandelen', { defaultValue: 'Afhandelen' })}
                            </Button>
                          ) : (
                            <div className="flex items-center justify-end gap-2">
                              <span className="text-xs text-gray-500">
                                {t(`stripeIncidents.afhandeling.${incident.resolution}`, {
                                  defaultValue: incident.resolution,
                                })}
                              </span>
                              <button
                                onClick={() =>
                                  afhandelMutatie.mutate({ id: incident.id, resolution: 'open', notes: '' })
                                }
                                className="text-xs text-blue-600 hover:underline"
                              >
                                {t('stripeIncidents.heropenen', { defaultValue: 'heropenen' })}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {afhandelModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('stripeIncidents.modalTitel', { defaultValue: 'Incident afhandelen' })}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {euro(afhandelModal.amount)}
              {afhandelModal.invoice_number ? ` — ${afhandelModal.invoice_number}` : ''}
              {afhandelModal.professional_name ? ` — ${afhandelModal.professional_name}` : ''}
            </p>

            <div className="mt-4 space-y-2">
              {AFHANDELINGEN.map((optie) => (
                <label
                  key={optie}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${
                    gekozenAfhandeling === optie ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="afhandeling"
                    className="mt-1"
                    checked={gekozenAfhandeling === optie}
                    onChange={() => setGekozenAfhandeling(optie)}
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">
                      {t(`stripeIncidents.afhandeling.${optie}`, { defaultValue: optie })}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {t(`stripeIncidents.afhandelingUitleg.${optie}`, { defaultValue: '' })}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <label className="block mt-4">
              <span className="text-sm font-medium text-gray-700">
                {t('stripeIncidents.notitie', { defaultValue: 'Toelichting' })}
              </span>
              <textarea
                value={notitie}
                onChange={(e) => setNotitie(e.target.value)}
                rows={3}
                className="mt-1 w-full border border-gray-300 rounded-lg p-2 text-sm"
                placeholder={t('stripeIncidents.notitiePlaceholder', {
                  defaultValue: 'Wat is er afgesproken? Wordt gelogd bij het incident.',
                })}
              />
            </label>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={sluitModal} disabled={afhandelMutatie.isPending}>
                {t('stripeIncidents.annuleren', { defaultValue: 'Annuleren' })}
              </Button>
              <Button
                onClick={() =>
                  afhandelMutatie.mutate({
                    id: afhandelModal.id,
                    resolution: gekozenAfhandeling,
                    notes: notitie,
                  })
                }
                disabled={afhandelMutatie.isPending}
              >
                {afhandelMutatie.isPending ? (
                  <RotateCcw className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {t('stripeIncidents.bevestigen', { defaultValue: 'Afhandelen' })}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminStripeIncidentsPage;
